import { useEffect, useRef, useState } from 'react';
import './DocumentCard.css';

interface DocumentCardProps {
  /** Filename, e.g. "lobby-incident.md". */
  name: string;
  /** Presigned download URL. */
  url: string;
  /** ISO expiry timestamp; '' when unknown (then no countdown is shown). */
  expiresAt: string;
  /** Ask the agent to mint a fresh link once this one has expired. */
  onRequestNew: () => void;
}

/** Total validity window (ms) parsed from the presigned URL's `X-Amz-Expires`,
 *  so the progress bar knows what "full" means. Null when not parseable. */
function totalWindowMs(url: string): number | null {
  try {
    const secs = Number(new URL(url).searchParams.get('X-Amz-Expires'));
    return Number.isFinite(secs) && secs > 0 ? secs * 1000 : null;
  } catch {
    return null;
  }
}

/** Format a remaining duration as `m:ss`, or `h:mm:ss` past an hour. */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  }
  return `${m}:${ss}`;
}

/**
 * A shared document in the chat stream: filename, a live countdown + progress
 * bar to the presigned URL's expiry, and an Open button. Best-effort auto-opens
 * the document in a new tab when it first appears (popup blockers usually
 * prevent this — the Open button is the reliable path). On expiry it flips to a
 * "link expired" state with a button that asks the agent for a fresh link.
 */
export default function DocumentCard({ name, url, expiresAt, onRequestNew }: DocumentCardProps) {
  const expiryMs = expiresAt ? Date.parse(expiresAt) : NaN;
  const hasExpiry = Number.isFinite(expiryMs);
  const windowMs = totalWindowMs(url);
  const [now, setNow] = useState(() => Date.now());
  const autoOpenedRef = useRef(false);

  // Best-effort auto-open once, when a still-valid link first arrives. A stream
  // event isn't a user gesture, so popup blockers usually stop this; the Open
  // button below is the dependable fallback.
  useEffect(() => {
    if (autoOpenedRef.current) {
      return;
    }
    autoOpenedRef.current = true;
    // Only auto-open a genuine presigned link that's still valid (not a
    // placeholder); popup blockers handle the rest.
    if (/X-Amz-Signature=/.test(url) && (!hasExpiry || expiryMs > Date.now())) {
      try {
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch {
        // Blocked or unavailable — the card's Open button covers it.
      }
    }
  }, [url, expiryMs, hasExpiry]);

  // Tick the countdown each second until expiry.
  const remaining = hasExpiry ? Math.max(0, expiryMs - now) : null;
  const expired = remaining !== null && remaining <= 0;
  useEffect(() => {
    if (!hasExpiry || expired) {
      return;
    }
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [hasExpiry, expired]);

  const pct =
    remaining !== null && windowMs
      ? Math.max(0, Math.min(100, (remaining / windowMs) * 100))
      : null;
  // Bar turns warm as time runs low.
  const low = pct !== null && pct <= 20;

  return (
    <div className={`om-doc${expired ? ' om-doc--expired' : ''}`}>
      <div className="om-doc__head">
        <span className="om-doc__icon" aria-hidden="true">
          ▤
        </span>
        <span className="om-doc__name" title={name}>
          {name}
        </span>
        {!expired ? (
          <a className="om-doc__open" href={url} target="_blank" rel="noopener noreferrer">
            Open
          </a>
        ) : (
          <button type="button" className="om-doc__renew" onClick={onRequestNew}>
            Request new link
          </button>
        )}
      </div>

      {expired ? (
        <div className="om-doc__status om-doc__status--expired">
          <span className="om-dot om-dot--err" />
          Link expired
        </div>
      ) : remaining !== null ? (
        <div className="om-doc__status">
          <div className="om-doc__meter" role="progressbar" aria-label="Time until link expires">
            <span
              className={`om-doc__meter-fill${low ? ' om-doc__meter-fill--low' : ''}`}
              style={{ width: `${pct ?? 100}%` }}
            />
          </div>
          <span className="om-doc__time">expires in {formatRemaining(remaining)}</span>
        </div>
      ) : (
        <div className="om-doc__status">
          <span className="om-doc__time">download link</span>
        </div>
      )}
    </div>
  );
}
